// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef } from 'react';

import type { EvolveApiRoute, EvolveApiResponse } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import { useSearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/useSearchableSelect';
import type { KeysOfType } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { weakOmit } from 'helpers/omit';

import type { TypeSafeRenderOption, WrappedSelectProps } from '../types';
import { WrappedSelect } from '../WrappedSelect';
import type { SearchableSelectorProps } from './types';

export type SearchableSelectProps<
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
> = SearchableSelectorProps<Route, T, TKey> &
  Omit<WrappedSelectProps<string, object>, keyof SearchableSelectorProps<Route, T, TKey> | 'data' | 'renderOption'> & {
    disableSearch?: boolean;
    getGroup?: (data: T) => string | undefined;
    nowrap?: boolean;
    preloadData?: boolean;
    renderOption?: TypeSafeRenderOption<string, { original: T }>;
  };

/**
 * Used for Selects whose "search" functionality
 * involves fetching data from the backend.
 */
export const SearchableSelect = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
>(
  props: SearchableSelectProps<Route, T, TKey>,
) => {
  const {
    idKey,
    value: parentValue,
    onChange: parentOnChange,
    defaultValue: parentDefaultValue,
    onOptionSubmit,
    disableSearch = false,
    placeholder = 'Type to search...',
    nothingFoundMessage = 'No results found',
    ...selectProps
  } = props;
  const ref = useRef<HTMLInputElement>(null);

  const [__value, __onChange] = useUncontrolled<T | null>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });

  const { options, latestDataLoaded, data, overrideKeys, overrideProps, fetchNextPage } = useSearchableSelect<
    Route,
    T,
    TKey
  >({ type: 'single', __value, __onChange }, props);

  const findOption = (id: T[TKey]) =>
    (__value?.[idKey] === id ? __value : options.find((o) => o[idKey] === id)) ?? null;

  return (
    <WrappedSelect
      ref={ref}
      placeholder={placeholder}
      nothingFoundMessage={disableSearch ? 'Start typing...' : !latestDataLoaded ? '...' : nothingFoundMessage}
      searchable
      noFilterOnSearch
      onClear={() => __onChange(null)}
      clearSearchOnFocus={false}
      {...weakOmit(selectProps, overrideKeys)}
      {...overrideProps}
      onFocus={(...e) => {
        overrideProps.onDropdownOpen();
        selectProps.onFocus?.(...e);
      }}
      onBlur={(...e) => {
        overrideProps.onDropdownClose();
        selectProps.onBlur?.(...e);
      }}
      onChange={(id) => __onChange(findOption(id as T[TKey]))}
      onOptionSubmit={(id) => {
        if (isNotNil(onOptionSubmit)) {
          const selected = findOption(id as T[TKey]);
          if (isNotNil(selected)) {
            onOptionSubmit(selected);
          }
        }
      }}
      value={(__value?.[idKey] as string | undefined) ?? null}
      data={data}
      onScrollToEnd={fetchNextPage}
    />
  );
};
